import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, CalendarClock, Sparkles, AlertTriangle } from 'lucide-react';
import { isoToDisplay } from '../components/ClassMappingManager';

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold text-[#475569]">{label}{required && <span className="text-[#DC2626]"> *</span>}</span>
    {children}
  </label>
);
const inputCls = 'w-full rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-action focus:ring-2 focus:ring-action/15';
const SectionTitle = ({ children }) => <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-navy">{children}</h3>;

function calcAge(dobStr, refStr) {
  if (!dobStr || !refStr) return null;
  const dob = new Date(dobStr + 'T00:00:00');
  const ref = new Date(refStr + 'T00:00:00');
  if (isNaN(dob) || isNaN(ref)) return null;
  if (dob > ref) return { invalid: true };
  let years = ref.getFullYear() - dob.getFullYear();
  let months = ref.getMonth() - dob.getMonth();
  let days = ref.getDate() - dob.getDate();
  if (days < 0) { months -= 1; const pm = new Date(ref.getFullYear(), ref.getMonth(), 0); days += pm.getDate(); }
  if (months < 0) { years -= 1; months += 12; }
  const total = years + months / 12 + days / 365.25;
  return { years, months, days, total };
}

const fmtRef = (r) => r ? new Date(r + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

export default function CreateCase() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [form, setForm] = useState({
    applicant_name: '', dob: '', class_name: '', previous_school: '', previous_class: '',
    contact_number: '', mother_tongue: '', parent_name: '', parent_email: '', address: '',
    enquiry_source: '', remarks: '', admission_form_number: '', form_issue_date: '',
    assigned_counsellor: '', session: '', form_issued: false, class_override_reason: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings').then((r) => { setSettings(r.data); setForm((f) => ({ ...f, session: r.data.current_session, assigned_counsellor: user.name })); });
    api.get('/counsellors').then((r) => setStaff(r.data));
    api.get('/class-mappings', { params: { active: true } }).then((r) => setMappings(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const classes = useMemo(() => (settings?.classes || []).slice().sort((a, b) => a.order - b.order), [settings]);
  const refDate = settings?.reference_dates?.[form.session] || (form.session ? `${form.session.split('-')[0]}-01-01` : null);
  const age = useMemo(() => calcAge(form.dob, refDate), [form.dob, refDate]);

  const recMapping = useMemo(() => {
    if (!form.dob || !mappings.length) return null;
    const ms = mappings.filter((m) => m.active !== false && (!m.session || m.session === form.session) && m.dob_from <= form.dob && form.dob <= m.dob_to);
    if (!ms.length) return null;
    ms.sort((a, b) => (b.session ? 1 : 0) - (a.session ? 1 : 0));
    return ms[0];
  }, [form.dob, form.session, mappings]);

  const recommended = useMemo(() => {
    if (mappings.length) return recMapping ? recMapping.class_name : null;
    if (!age || age.invalid || !classes.length) return null;
    const c = classes.find((c) => c.active !== false && c.min_age != null && age.total >= c.min_age && age.total < c.max_age);
    return c ? c.name : null;
  }, [recMapping, mappings, age, classes]);

  const testRequired = classes.find((c) => c.name === form.class_name)?.test_required;
  const isOverride = recommended && form.class_name && form.class_name !== recommended;
  const overrideRequired = settings?.override_reason_required !== false;

  const submit = async () => {
    for (const f of ['applicant_name', 'dob', 'class_name', 'contact_number', 'mother_tongue']) {
      if (!form[f]) { toast.error('Please fill all required fields'); return; }
    }
    if (age?.invalid) { toast.error('Please enter a valid date of birth.'); return; }
    if (isOverride && overrideRequired && !form.class_override_reason.trim()) {
      toast.error('Please provide a reason for choosing a different class'); return;
    }
    setSaving(true);
    try {
      const r = await api.post('/cases', { ...form, system_recommended_class: recommended });
      toast.success(`Case ${r.data.case_number} created`);
      navigate(`/cases/${r.data.case_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create case'); }
    finally { setSaving(false); }
  };

  if (!settings) return <p className="text-sm text-[#94A3B8]">Loading…</p>;

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="create-case-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-action"><ArrowLeft size={16} /> Back</button>
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#172033]">New Admission Case</h1>
        <p className="text-sm text-[#64748B]">Quick applicant intake — takes under a minute.</p>
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
        {/* SECTION 1 — APPLICANT */}
        <SectionTitle>1 · Applicant</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Applicant Name" required><input data-testid="input-applicant-name" className={inputCls} value={form.applicant_name} onChange={(e) => set('applicant_name', e.target.value)} /></Field>
          <Field label="Date of Birth" required><input data-testid="input-dob" type="date" className={inputCls} value={form.dob} onChange={(e) => set('dob', e.target.value)} /></Field>
        </div>

        {/* Age calculator */}
        {form.dob && (
          <div className="mt-4 rounded-xl border border-action/20 bg-action/5 p-4" data-testid="age-calculator">
            <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-action">
              <CalendarClock size={15} /> Age calculated as on {fmtRef(refDate)}
            </div>
            {age?.invalid ? (
              <p className="text-sm font-semibold text-[#DC2626]">Please enter a valid date of birth.</p>
            ) : age ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div><p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">Date of Birth</p><p className="text-sm font-semibold text-[#172033]">{new Date(form.dob + 'T00:00:00').toLocaleDateString('en-GB')}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">Age on {fmtRef(refDate)}</p><p data-testid="age-value" className="text-sm font-semibold text-[#172033]">{age.years} Years {age.months} Months {age.days} Days</p></div>
                  <div><p className="text-[10px] uppercase tracking-wide text-[#94A3B8]">System Recommendation</p><p data-testid="recommended-class" className="text-sm font-bold text-teal">{recommended || '—'}</p></div>
                </div>
                {recMapping && (
                  <p data-testid="recommended-class-reason" className="mt-3 rounded-lg bg-white/70 p-2.5 text-xs text-[#475569]">
                    Suggested because DOB <b>{isoToDisplay(form.dob)}</b> falls within the eligible range for <b>{recMapping.class_name}</b>: {isoToDisplay(recMapping.dob_from)} – {isoToDisplay(recMapping.dob_to)}{recMapping.session ? ` (Session ${recMapping.session})` : ' (all sessions)'}. Both boundaries are inclusive.
                  </p>
                )}
                {!recommended && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg bg-[#FEF3C7] p-2.5 text-xs text-[#92400E]"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {mappings.length ? 'Date of birth does not fall within any active class mapping range for this session.' : 'Age does not currently match a configured class range.'} Please review the date of birth or select a class manually.</div>
                )}
                {recommended && (
                  <button type="button" data-testid="use-recommended-class" onClick={() => set('class_name', recommended)}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-teal px-3 py-1.5 text-xs font-semibold text-white hover:bg-brandteal-hover">
                    <Sparkles size={13} /> Use Recommended Class
                  </button>
                )}
              </>
            ) : null}
          </div>
        )}

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Class Recommended / Prescribed" required>
            <select data-testid="input-class" className={inputCls} value={form.class_name} onChange={(e) => set('class_name', e.target.value)}>
              <option value="">Select class</option>
              {classes.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          {form.class_name && (
            <div className="flex items-end">
              <p className="pb-2 text-xs font-medium" style={{ color: testRequired ? '#7C3AED' : '#0F766E' }}>
                {testRequired ? '● Admission test required for this class.' : '● No admission test for this class.'}
              </p>
            </div>
          )}
        </div>
        {isOverride && (
          <div className="mt-3 rounded-xl border border-[#FDE68A] bg-[#FEF3C7]/50 p-3">
            <p className="mb-1.5 text-xs font-semibold text-[#92400E]">You selected <b>{form.class_name}</b> instead of the recommended <b>{recommended}</b>.</p>
            <Field label={`Reason for override${overrideRequired ? '' : ' (optional)'}`} required={overrideRequired}>
              <input data-testid="override-reason" className={inputCls} value={form.class_override_reason} onChange={(e) => set('class_override_reason', e.target.value)} placeholder="e.g. Previous school placement / academic assessment" />
            </Field>
          </div>
        )}

        {/* SECTION 2 — PARENT / CONTACT */}
        <SectionTitle>2 · Parent / Contact</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Parent / Guardian Name"><input data-testid="input-parent-name" className={inputCls} value={form.parent_name} onChange={(e) => set('parent_name', e.target.value)} /></Field>
          <Field label="Registered Mobile Number" required><input data-testid="input-contact" type="tel" inputMode="numeric" className={inputCls} value={form.contact_number} onChange={(e) => set('contact_number', e.target.value)} placeholder="10-digit mobile used for Parent Portal login" /></Field>
          <Field label="Email"><input data-testid="input-parent-email" type="email" className={inputCls} value={form.parent_email} onChange={(e) => set('parent_email', e.target.value)} /></Field>
          <Field label="Mother Tongue" required><input data-testid="input-mother-tongue" className={inputCls} value={form.mother_tongue} onChange={(e) => set('mother_tongue', e.target.value)} /></Field>
        </div>

        {/* SECTION 3 — EDUCATION */}
        <SectionTitle>3 · Education</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Previous School"><input data-testid="input-prev-school" className={inputCls} value={form.previous_school} onChange={(e) => set('previous_school', e.target.value)} /></Field>
          <Field label="Previous Class (if applicable)"><input data-testid="input-prev-class" className={inputCls} value={form.previous_class} onChange={(e) => set('previous_class', e.target.value)} /></Field>
        </div>

        {/* SECTION 4 — ADMISSION */}
        <SectionTitle>4 · Admission</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Academic Session">
            <select className={inputCls} value={form.session} onChange={(e) => set('session', e.target.value)}>
              {settings.sessions?.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Responsible Officer">
            <select className={inputCls} value={form.assigned_counsellor} onChange={(e) => set('assigned_counsellor', e.target.value)}>
              <option value="">Unassigned</option>
              {staff.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Enquiry Source"><input className={inputCls} value={form.enquiry_source} onChange={(e) => set('enquiry_source', e.target.value)} placeholder="Walk-in, Website, Referral…" /></Field>
          <Field label="Remarks"><input className={inputCls} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} /></Field>
        </div>
        <p className="mb-2 mt-4 text-sm text-[#475569]">Has the admission form been issued?</p>
        <div className="flex gap-3">
          {[['Yes', true], ['No', false]].map(([lbl, val]) => (
            <button key={lbl} data-testid={`form-issued-${lbl.toLowerCase()}`} onClick={() => set('form_issued', val)}
              className={`rounded-lg border px-6 py-2 text-sm font-semibold transition ${form.form_issued === val ? 'border-action bg-action text-white' : 'border-[#E2E8F0] bg-white text-[#475569] hover:border-[#CBD5E1]'}`}>{lbl}</button>
          ))}
        </div>
        {form.form_issued && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Form Issue Date"><input type="date" className={inputCls} value={form.form_issue_date} onChange={(e) => set('form_issue_date', e.target.value)} /></Field>
            <Field label="Admission Form Number"><input className={inputCls} value={form.admission_form_number} onChange={(e) => set('admission_form_number', e.target.value)} /></Field>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3 border-t border-[#E2E8F0] pt-5">
          <button onClick={() => navigate(-1)} className="rounded-lg border border-[#E2E8F0] px-5 py-2.5 text-sm font-semibold text-[#475569] hover:bg-[#F8FAFC]">Cancel</button>
          <button data-testid="submit-case-button" disabled={saving} onClick={submit}
            className="flex items-center gap-2 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark disabled:opacity-60">
            <UserPlus size={16} /> {saving ? 'Creating…' : 'Create Case'}
          </button>
        </div>
      </div>
    </div>
  );
}
